import type { TripEvent } from '../types.js';

const g = (q: string) =>
	`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
const k = (q: string) => `https://map.kakao.com/?q=${encodeURIComponent(q)}`;
const n = (q: string) => `https://map.naver.com/p/search/${encodeURIComponent(q)}`;
const loc = (name: string, query: string, area?: string) => ({
	name,
	area,
	google: g(query),
	kakao: k(query),
	naver: n(query),
});

/** Full itinerary, the single source of truth (per person; ZAR ≈ KRW / 93). */
export const EVENTS: TripEvent[] = [
	// ───────────────────────── SEOUL ─────────────────────────
	// Sat 18 Jul — Fly out: Johannesburg → Dubai
	{
		id: '2026-07-18-jnb-dxb',
		city: 'seoul',
		date: '2026-07-18',
		start: '13:30',
		end: '23:40',
		title: 'Fly Johannesburg → Dubai · EK762',
		summary:
			'Check-in 10:00 (SAST). Depart OR Tambo (JNB) 13:30 SAST → arrive Dubai (DXB) 23:40 GST. ~8 hr, then a short overnight layover.',
		category: 'travel',
		location: loc('OR Tambo International Airport', 'OR Tambo International Airport Johannesburg'),
	},

	// Sun 19 Jul — Dubai → Seoul, then arrival
	{
		id: '2026-07-19-dxb-icn',
		city: 'seoul',
		date: '2026-07-19',
		start: '03:40',
		end: '17:00',
		title: 'Fly Dubai → Seoul · EK322',
		summary:
			'Check-in 00:40 (GST). Depart Dubai (DXB) 03:40 GST → land Incheon (ICN) 17:00 KST. ~9 hr.',
		category: 'travel',
		location: loc('Incheon International Airport', 'Incheon International Airport'),
	},
	{
		id: '2026-07-19-arrive-icn',
		city: 'seoul',
		date: '2026-07-19',
		start: '17:00',
		end: '18:00',
		title: 'Land at Incheon (ICN)',
		summary:
			'Immigration + baggage ~1 hr. Connect to the airport Wi-Fi and message your driver once through.',
		category: 'travel',
	},
	{
		id: '2026-07-19-checkin',
		city: 'seoul',
		date: '2026-07-19',
		start: '18:00',
		end: '19:30',
		title: 'Check in — DH Naissance Hotel',
		summary: 'Seongbuk-gu, near Hansung University Stn (Line 4). Freshen up.',
		category: 'relax',
		location: loc('Friendly DH Naissance Hotel', 'Friendly DH Naissance Hotel Dongsomun-ro Seoul'),
	},
	{
		id: '2026-07-19-dinner-daehangno',
		city: 'seoul',
		date: '2026-07-19',
		start: '20:00',
		end: '21:30',
		title: 'Relaxed dinner — Daehangno / Seongbuk-dong',
		summary: 'Cheap, lively student eateries near the hotel. Early night to beat jet lag.',
		category: 'food',
		cost: { krw: 15000, zar: 161 },
		location: loc('Marronnier Park Daehangno', 'Marronnier Park Daehangno Seoul'),
	},
	{
		id: '2026-07-19-naksan',
		city: 'seoul',
		date: '2026-07-19',
		start: '21:30',
		end: '22:00',
		title: 'Naksan Park city-wall night view',
		summary: 'Optional 10-min stroll for a free night skyline, if not wiped out.',
		category: 'relax',
		optional: true,
		cost: { krw: 0, zar: 0 },
		location: loc('Naksan Park', 'Naksan Park Seoul'),
	},

	// Mon 20 Jul — Explore Day (Gangnam / COEX)
	{
		id: '2026-07-20-breakfast',
		city: 'seoul',
		date: '2026-07-20',
		start: '08:00',
		end: '09:00',
		title: 'Breakfast at hotel',
		category: 'food',
		cost: { krw: 0, zar: 0, note: 'included' },
	},
	{
		id: '2026-07-20-tmoney',
		city: 'seoul',
		date: '2026-07-20',
		start: '09:00',
		end: '09:30',
		title: 'Get T-money cards',
		summary:
			'Buy and top up at any convenience store (CU, GS25, 7-Eleven). Works on metro, buses and many taxis in Seoul & Busan.',
		category: 'travel',
		cost: { krw: 4000, zar: 43, note: 'card + top-up' },
	},
	{
		id: '2026-07-20-coex',
		city: 'seoul',
		date: '2026-07-20',
		start: '10:00',
		end: '12:30',
		title: 'COEX — Starfield Library, K-pop Square, Gangnam Style statue, MY Art Museum',
		summary:
			'All around the COEX / Gangnam complex. Free to wander the mall & library; MY Art Museum has ticketed exhibitions.',
		category: 'sightseeing',
		cost: { krw: 0, zar: 0 },
		location: loc('Starfield Library COEX', 'Starfield Library COEX'),
	},
	{
		id: '2026-07-20-ring-class',
		city: 'seoul',
		date: '2026-07-20',
		start: '13:00',
		end: '14:30',
		title: 'Ring-making class at BlingBox',
		summary:
			'37 Bongeunsa-ro 2-gil, Gangnam-gu · 5th floor, behind the CGV cinema, next to Starbucks. Note: ~55-min commute from Gangnam to BlingBox.',
		category: 'sightseeing',
		cost: { krw: 50685, zar: 545 },
		booking: 'https://gyg.me/hajBZ7aq',
		location: loc('BlingBox Ring Making', '37 Bongeunsa-ro 2-gil Gangnam-gu Seoul'),
	},
	{
		id: '2026-07-20-chopstick',
		city: 'seoul',
		date: '2026-07-20',
		start: '16:00',
		end: '17:00',
		title: 'Chopstick-making workshop',
		summary: 'Craft your own pair of Korean chopsticks. Insadong / Jongno area.',
		category: 'sightseeing',
		cost: { krw: 20274, zar: 218 },
		booking: 'https://gyg.me/CgjXvsn3',
		location: loc('Chopstick-making workshop', 'Insadong Seoul'),
	},
	{
		id: '2026-07-20-dinner',
		city: 'seoul',
		date: '2026-07-20',
		start: '18:00',
		end: '20:00',
		title: 'Explore for dinner, then head home',
		summary: 'Wander for a bite wherever the evening lands you.',
		category: 'food',
		cost: { krw: 18000, zar: 194 },
	},

	// Tue 21 Jul — Everland (full day)
	{
		id: '2026-07-21-depart',
		city: 'seoul',
		date: '2026-07-21',
		start: '08:30',
		end: '10:00',
		title: 'Leave hotel for Everland',
		summary: 'Leave the hotel by 08:30 — it is roughly a 2-hour commute to Yongin.',
		category: 'travel',
	},
	{
		id: '2026-07-21-everland',
		city: 'seoul',
		date: '2026-07-21',
		start: '10:00',
		end: '22:00',
		title: 'Everland Theme Park — full day',
		summary:
			'T-Express, Lost Valley safari, Panda World, evening parade & fireworks. Leave the park by 22:00.',
		category: 'sightseeing',
		cost: { krw: 35805, zar: 385, note: 'Klook' },
		booking: 'https://s.klook.com/c/5yKl5lpn3b',
		location: loc('Everland', 'Everland Theme Park'),
	},

	// Wed 22 Jul — Seoul Landmarks Tour (guided, Package 2) — one card per stop
	{
		id: '2026-07-22-pickup',
		city: 'seoul',
		date: '2026-07-22',
		start: '08:50',
		end: '09:20',
		title: 'Seoul Highlights tour — pickup at THE PLAZA',
		summary:
			'Be there by 08:50 (10 min early). Full-day guided coach tour: English-speaking guide, palace & museum entry, and the Namsan cable car all included. Drop-off later at Myeongdong / The Plaza / City Hall. The stops below are all part of this one booking.',
		category: 'travel',
		cost: { krw: 83514, zar: 898, note: 'Package 2 · whole day' },
		booking: 'https://gyg.me/2kyDa6jE',
		location: loc('THE PLAZA Seoul, Autograph Collection', 'THE PLAZA Seoul Autograph Collection'),
	},
	{
		id: '2026-07-22-jogyesa',
		city: 'seoul',
		date: '2026-07-22',
		start: '09:20',
		end: '09:50',
		title: 'Jogyesa Temple',
		summary: 'Seoul’s main Zen Buddhist temple — guided visit (~30 min).',
		category: 'sightseeing',
		location: loc('Jogyesa Temple', 'Jogyesa Temple Seoul'),
	},
	{
		id: '2026-07-22-gyeongbokgung',
		city: 'seoul',
		date: '2026-07-22',
		start: '09:50',
		end: '10:50',
		title: 'Gyeongbokgung Palace',
		summary: 'The grand royal palace — guided tour (~1 hr). Entry included.',
		category: 'sightseeing',
		location: loc('Gyeongbokgung Palace', 'Gyeongbokgung Palace'),
	},
	{
		id: '2026-07-22-folk-museum',
		city: 'seoul',
		date: '2026-07-22',
		start: '10:50',
		end: '11:20',
		title: 'National Folk Museum of Korea',
		summary: 'On the palace grounds — guided visit (~30 min).',
		category: 'museum',
		location: loc('National Folk Museum of Korea', 'National Folk Museum of Korea'),
	},
	{
		id: '2026-07-22-ginseng',
		city: 'seoul',
		date: '2026-07-22',
		start: '11:20',
		end: '11:50',
		title: 'Korean Ginseng Museum',
		summary: 'Ginseng & health-product shopping stop (~30 min).',
		category: 'shopping',
		location: loc('Korean Ginseng Museum', 'Korean Ginseng Museum Seoul'),
	},
	{
		id: '2026-07-22-insadong',
		city: 'seoul',
		date: '2026-07-22',
		start: '12:20',
		end: '13:20',
		title: 'Insadong',
		summary: 'Arts & crafts market — free time (~1 hr) after a short coach ride.',
		category: 'shopping',
		location: loc('Insadong', 'Insadong Seoul'),
	},
	{
		id: '2026-07-22-gwangjang',
		city: 'seoul',
		date: '2026-07-22',
		start: '13:20',
		end: '14:00',
		title: 'Gwangjang Market',
		summary: 'Famous street-food market — lunch on the go (~40 min).',
		category: 'food',
		location: loc('Gwangjang Market', 'Gwangjang Market Seoul'),
	},
	{
		id: '2026-07-22-namsangol',
		city: 'seoul',
		date: '2026-07-22',
		start: '14:00',
		end: '14:20',
		title: 'Namsangol Hanok Village',
		summary: 'Restored traditional houses at the foot of Namsan (~20 min).',
		category: 'sightseeing',
		location: loc('Namsangol Hanok Village', 'Namsangol Hanok Village'),
	},
	{
		id: '2026-07-22-nseoul',
		city: 'seoul',
		date: '2026-07-22',
		start: '14:20',
		end: '15:20',
		title: 'N Seoul Tower',
		summary: 'Round-trip Namsan cable car included — city views from the tower (~1 hr).',
		category: 'sightseeing',
		location: loc('N Seoul Tower', 'N Seoul Tower'),
	},
	{
		id: '2026-07-22-dinner',
		city: 'seoul',
		date: '2026-07-22',
		start: '18:00',
		end: '19:30',
		title: 'Dinner near Myeongdong',
		summary: 'Grab dinner around the tour drop-off before heading back.',
		category: 'food',
		cost: { krw: 18000, zar: 194 },
		location: loc('Myeongdong', 'Myeongdong Seoul'),
	},

	// Thu 23 Jul — Galleries & Shopping
	{
		id: '2026-07-23-breakfast',
		city: 'seoul',
		date: '2026-07-23',
		start: '08:00',
		end: '09:00',
		title: 'Breakfast',
		category: 'food',
		cost: { krw: 0, zar: 0, note: 'included' },
	},
	{
		id: '2026-07-23-seoul-forest',
		city: 'seoul',
		date: '2026-07-23',
		start: '09:30',
		end: '11:00',
		title: 'Seoul Forest',
		summary: 'Riverside park — deer enclosure, gardens, trails.',
		category: 'nature',
		cost: { krw: 0, zar: 0 },
		location: loc('Seoul Forest', 'Seoul Forest'),
	},
	{
		id: '2026-07-23-seongsu',
		city: 'seoul',
		date: '2026-07-23',
		start: '11:00',
		end: '14:00',
		title: 'Seongsu-dong — Olive Young, concept stores, cute cafés + lunch',
		summary:
			"Seoul's 'Brooklyn' — flagship Olive Young for K-beauty, pop-ups, and photogenic cafés for lunch.",
		category: 'shopping',
		cost: { krw: 25000, zar: 269 },
		location: loc('Seongsu-dong café street', 'Seongsu-dong cafe street'),
	},
	{
		id: '2026-07-23-mmca',
		city: 'seoul',
		date: '2026-07-23',
		start: '14:30',
		end: '16:00',
		title: 'MMCA Seoul (modern art, Samcheong)',
		summary: 'Closed Mondays — done today.',
		category: 'museum',
		cost: { krw: 4000, zar: 43 },
		location: loc('MMCA Seoul', 'MMCA Seoul'),
	},
	{
		id: '2026-07-23-smh',
		city: 'seoul',
		date: '2026-07-23',
		start: '16:00',
		end: '17:30',
		title: 'Seoul Museum of History',
		summary: 'Free; closed Mondays. A quick, well-curated city history stop.',
		category: 'museum',
		cost: { krw: 0, zar: 0 },
		location: loc('Seoul Museum of History', 'Seoul Museum of History'),
	},
	{
		id: '2026-07-23-dongdaemun',
		city: 'seoul',
		date: '2026-07-23',
		start: '18:30',
		end: '21:00',
		title: 'Dongdaemun shopping + dinner',
		summary: 'Late-night malls (DDP area) — fashion, accessories, street food.',
		category: 'shopping',
		cost: { krw: 20000, zar: 215 },
		location: loc('Dongdaemun Design Plaza (DDP)', 'Dongdaemun Design Plaza Seoul'),
	},

	// Fri 24 Jul — Catch-up Day
	{
		id: '2026-07-24-breakfast',
		city: 'seoul',
		date: '2026-07-24',
		start: '08:00',
		end: '09:00',
		title: 'Breakfast',
		category: 'food',
		cost: { krw: 0, zar: 0, note: 'included' },
	},
	{
		id: '2026-07-24-catchup',
		city: 'seoul',
		date: '2026-07-24',
		start: '09:30',
		end: '10:30',
		title: 'Catch-up + get souvenirs',
		summary: 'Mop up anything missed during the week and grab souvenirs.',
		category: 'shopping',
		cost: { krw: 0, zar: 0 },
	},
	{
		id: '2026-07-24-lotteworld',
		city: 'seoul',
		date: '2026-07-24',
		start: '11:00',
		end: '16:00',
		title: 'Lotte World',
		summary: "Huge indoor/outdoor theme park at Jamsil. Magic Island by the lake.",
		category: 'sightseeing',
		cost: { krw: 62000, zar: 667, note: 'day pass, est.' },
		location: loc('Lotte World', 'Lotte World Adventure Jamsil'),
	},
	{
		id: '2026-07-24-courier',
		city: 'seoul',
		date: '2026-07-24',
		start: '16:30',
		end: '17:30',
		title: 'Courier parcel to Katz',
		summary: 'Send the stuff off to Katz before the domestic legs of the trip.',
		category: 'travel',
	},
	{
		id: '2026-07-24-banpo',
		city: 'seoul',
		date: '2026-07-24',
		start: '20:00',
		end: '20:20',
		title: 'Banpo Bridge Moonlight Rainbow Fountain (8 PM show)',
		summary: 'Free 20-min light show; best once dark. Weather-dependent.',
		category: 'show',
		cost: { krw: 0, zar: 0 },
		location: loc('Banpo Bridge Moonlight Rainbow Fountain', 'Banpo Bridge Moonlight Rainbow Fountain'),
	},
	{
		id: '2026-07-24-arrange-taxi',
		city: 'seoul',
		date: '2026-07-24',
		start: '21:00',
		end: '21:15',
		title: '⏰ Arrange tomorrow’s taxi to Gimpo with the hotel',
		summary:
			'Ask the hotel tonight to book a taxi for the morning run to Gimpo (GMP) for the 13:55 flight to Jeju.',
		category: 'travel',
	},

	// Sat 25 Jul — Fly to Jeju
	{
		id: '2026-07-25-checkout',
		city: 'seoul',
		date: '2026-07-25',
		start: '10:00',
		end: '12:00',
		title: 'Breakfast, pack, check out → taxi to Gimpo (GMP)',
		summary:
			'Take the pre-arranged hotel taxi. ~50 min to Gimpo; be there by ~12:00 (domestic, 1 hr before).',
		category: 'travel',
		location: loc('Gimpo Airport', 'Gimpo Airport'),
	},
	{
		id: '2026-07-25-flight-cju',
		city: 'jeju',
		date: '2026-07-25',
		start: '13:55',
		end: '15:10',
		title: 'Flight TW717 — Gimpo → Jeju',
		summary: '1h15. Collect the rental car on arrival.',
		category: 'travel',
	},
	{
		id: '2026-07-25-taxi-hotel',
		city: 'jeju',
		date: '2026-07-25',
		start: '15:10',
		end: '16:00',
		title: 'Taxi to the hotel, drop bags',
		summary: 'Arrive Jeju 15:10; taxi to the hotel and freshen up.',
		category: 'travel',
	},

	// ───────────────────────── JEJU ─────────────────────────
	{
		id: '2026-07-25-yongduam',
		city: 'jeju',
		date: '2026-07-25',
		start: '16:30',
		end: '18:00',
		title: 'Yongduam (Dragon Head) Rock + coast sunset',
		category: 'nature',
		cost: { krw: 0, zar: 0 },
		location: loc('Yongduam Rock', 'Yongduam Rock Jeju'),
	},
	{
		id: '2026-07-25-dongmun',
		city: 'jeju',
		date: '2026-07-25',
		start: '18:30',
		end: '20:30',
		title: 'Dongmun Market dinner',
		summary: 'Jeju black pork, fresh hwe (sashimi), hallabong, street food.',
		category: 'food',
		cost: { krw: 20000, zar: 215 },
		location: loc('Dongmun Traditional Market', 'Dongmun Traditional Market Jeju'),
	},

	// Sun 26 Jul — East Jeju (UNESCO + Udo)
	{
		id: '2026-07-26-manjanggul',
		city: 'jeju',
		date: '2026-07-26',
		start: '09:30',
		end: '11:00',
		title: 'Manjanggul Lava Tube (UNESCO)',
		summary: 'Walk ~1 km into the lava cave. 09:00–18:00.',
		category: 'nature',
		cost: { krw: 4000, zar: 43 },
		location: loc('Manjanggul Lava Tube', 'Manjanggul Lava Tube'),
	},
	{
		id: '2026-07-26-seongeup',
		city: 'jeju',
		date: '2026-07-26',
		start: '11:30',
		end: '12:30',
		title: 'Seongeup Folk Village',
		summary: 'Real historic stone-walled Jeju village, en route.',
		category: 'sightseeing',
		cost: { krw: 0, zar: 0 },
		location: loc('Seongeup Folk Village', 'Seongeup Folk Village'),
	},
	{
		id: '2026-07-26-lunch-seongsan',
		city: 'jeju',
		date: '2026-07-26',
		start: '12:30',
		end: '13:30',
		title: 'Lunch near Seongsan (seafood)',
		category: 'food',
		cost: { krw: 15000, zar: 161 },
	},
	{
		id: '2026-07-26-seongsan',
		city: 'jeju',
		date: '2026-07-26',
		start: '13:30',
		end: '15:00',
		title: 'Seongsan Ilchulbong (Sunrise Peak, UNESCO)',
		summary: 'Tuff-cone crater; ~30–45 min hike to the rim.',
		category: 'nature',
		cost: { krw: 5000, zar: 54 },
		location: loc('Seongsan Ilchulbong', 'Seongsan Ilchulbong'),
	},
	{
		id: '2026-07-26-udo',
		city: 'jeju',
		date: '2026-07-26',
		start: '15:30',
		end: '18:30',
		title: 'Udo Island (ferry from Seongsan Port)',
		summary: '~15-min crossing; rent an e-scooter. CHECK the last return ferry!',
		category: 'nature',
		cost: { krw: 10500, zar: 113, note: 'return ferry' },
		location: loc('Seongsan Port (Udo ferry)', 'Seongsan Port Udo ferry'),
	},
	{
		id: '2026-07-26-dinner',
		city: 'jeju',
		date: '2026-07-26',
		start: '19:30',
		end: '20:30',
		title: 'Drive back / dinner',
		summary: 'Alt to Udo: Seopjikoji cape + Aqua Planet aquarium.',
		category: 'food',
		cost: { krw: 15000, zar: 161 },
	},

	// Mon 27 Jul — Scuba diving (East Jeju)
	{
		id: '2026-07-27-breakfast',
		city: 'jeju',
		date: '2026-07-27',
		start: '07:30',
		end: '08:15',
		title: 'Breakfast, early start',
		category: 'food',
		cost: { krw: 0, zar: 0, note: 'included' },
	},
	{
		id: '2026-07-27-drive-seongsan',
		city: 'jeju',
		date: '2026-07-27',
		start: '08:15',
		end: '11:00',
		title: 'Drive east to Seongsan',
		summary: 'Roughly a 2.5-hour drive across the island to the dive resort.',
		category: 'travel',
	},
	{
		id: '2026-07-27-scuba',
		city: 'jeju',
		date: '2026-07-27',
		start: '11:00',
		end: '12:30',
		title: 'Scuba diving — Seongsan Scuba Resort (PADI)',
		summary: 'Guided dive off the east coast near Seongsan Ilchulbong.',
		category: 'sport',
		cost: { krw: 100347, zar: 1079 },
		booking: 'https://gyg.me/UkzwLmEW',
		location: loc('Seongsan Scuba Resort', 'Seongsan Scuba Resort Jeju'),
	},
	{
		id: '2026-07-27-lunch',
		city: 'jeju',
		date: '2026-07-27',
		start: '12:45',
		end: '13:45',
		title: 'Late lunch near Seongsan',
		summary: 'Refuel after the dive before the drive back.',
		category: 'food',
		cost: { krw: 15000, zar: 161 },
	},
	{
		id: '2026-07-27-coast-drive',
		city: 'jeju',
		date: '2026-07-27',
		start: '14:00',
		end: '17:00',
		title: 'Scenic coastal drive back to Jeju City',
		summary: 'Take the coast road; stop for a viewpoint or café on the way.',
		category: 'nature',
		cost: { krw: 0, zar: 0 },
	},
	{
		id: '2026-07-27-blackpork',
		city: 'jeju',
		date: '2026-07-27',
		start: '19:00',
		end: '20:30',
		title: 'Black Pork Street dinner',
		summary: 'Heukdwaeji-geori BBQ in Jeju City.',
		category: 'food',
		cost: { krw: 25000, zar: 269 },
		location: loc('Jeju Black Pork Street', 'Jeju Black Pork Street'),
	},
	{
		id: '2026-07-27-arrange-taxi',
		city: 'jeju',
		date: '2026-07-27',
		start: '21:00',
		end: '21:15',
		title: '⏰ Arrange tomorrow’s taxi to Jeju Airport with the hotel',
		summary:
			'Ask the hotel tonight to book a taxi for the morning run to Jeju (CJU) for the 10:45 flight to Busan.',
		category: 'travel',
	},

	// Tue 28 Jul — Fly to Busan
	{
		id: '2026-07-28-return-car',
		city: 'jeju',
		date: '2026-07-28',
		start: '08:00',
		end: '09:15',
		title: 'Breakfast, pack, taxi → Jeju Airport (CJU)',
		summary: 'Return the rental car and take the pre-arranged taxi. Be at CJU by ~09:15.',
		category: 'travel',
	},
	{
		id: '2026-07-28-flight-pus',
		city: 'busan',
		date: '2026-07-28',
		start: '10:45',
		end: '11:45',
		title: 'Flight 7C504 — Jeju → Busan (PUS)',
		summary: 'Arrive Busan 11:45; taxi to the hotel.',
		category: 'travel',
	},

	// ───────────────────────── BUSAN ─────────────────────────
	{
		id: '2026-07-28-arrive',
		city: 'busan',
		date: '2026-07-28',
		start: '11:45',
		end: '14:00',
		title: 'Arrive Busan, to hotel, drop bags',
		summary: 'Hotel Ososo, Dong-gu — by Busan Station. ~30–40 min from Gimhae.',
		category: 'travel',
		location: loc('Ososo (hotel)', 'Ososo Busanjinseonggongwon-ro Busan'),
	},
	{
		id: '2026-07-28-jagalchi',
		city: 'busan',
		date: '2026-07-28',
		start: '14:00',
		end: '16:00',
		title: 'Jagalchi Fish Market + BIFF Square',
		summary: "Korea's largest seafood market — pick fresh, eat upstairs.",
		category: 'food',
		cost: { krw: 20000, zar: 215 },
		location: loc('Jagalchi Market', 'Jagalchi Market Busan'),
	},
	{
		id: '2026-07-28-gukje',
		city: 'busan',
		date: '2026-07-28',
		start: '16:00',
		end: '18:00',
		title: 'Gukje Market + Yongdusan / Busan Tower',
		summary: 'Harbour views from the park.',
		category: 'sightseeing',
		cost: { krw: 0, zar: 0, note: 'Busan Tower obs ~₩12,000' },
		location: loc('Gukje Market', 'Gukje Market Busan'),
	},
	{
		id: '2026-07-28-dinner-nampo',
		city: 'busan',
		date: '2026-07-28',
		start: '18:00',
		end: '19:30',
		title: 'Seafood dinner in Nampo',
		category: 'food',
		cost: { krw: 25000, zar: 269 },
	},

	// Wed 29 Jul — West Busan
	{
		id: '2026-07-29-gamcheon',
		city: 'busan',
		date: '2026-07-29',
		start: '09:30',
		end: '12:00',
		title: 'Gamcheon Culture Village',
		summary: 'Colourful hillside village, murals, art shops. Stamp map ~₩2,000.',
		category: 'sightseeing',
		cost: { krw: 0, zar: 0, note: 'stamp map ₩2,000' },
		location: loc('Gamcheon Culture Village', 'Gamcheon Culture Village'),
	},
	{
		id: '2026-07-29-lunch',
		city: 'busan',
		date: '2026-07-29',
		start: '12:00',
		end: '13:00',
		title: 'Lunch',
		category: 'food',
		cost: { krw: 15000, zar: 161 },
	},
	{
		id: '2026-07-29-songdo',
		city: 'busan',
		date: '2026-07-29',
		start: '13:30',
		end: '15:30',
		title: 'Songdo Marine Cable Car + Skywalk',
		summary: 'Air Cruise over the bay. Crystal-floor cabin ₩22k.',
		category: 'sightseeing',
		cost: { krw: 17000, zar: 183, note: 'RT, general cabin' },
		location: loc('Songdo Marine Cable Car', 'Songdo Marine Cable Car Busan'),
	},
	{
		id: '2026-07-29-huinnyeoul',
		city: 'busan',
		date: '2026-07-29',
		start: '16:00',
		end: '18:30',
		title: 'Huinnyeoul Culture Village (Yeongdo) sunset',
		summary: "Cliff-top seaside village, the 'Santorini of Busan'. Optional Taejongdae nearby.",
		category: 'sightseeing',
		cost: { krw: 0, zar: 0 },
		location: loc('Huinnyeoul Culture Village', 'Huinnyeoul Culture Village'),
	},

	// Thu 30 Jul — East coast
	{
		id: '2026-07-30-yonggungsa',
		city: 'busan',
		date: '2026-07-30',
		start: '09:00',
		end: '11:00',
		title: 'Haedong Yonggungsa Temple',
		summary: 'Stunning temple built on the ocean rocks. Go earlyish.',
		category: 'sightseeing',
		cost: { krw: 0, zar: 0 },
		location: loc('Haedong Yonggungsa Temple', 'Haedong Yonggungsa Temple'),
	},
	{
		id: '2026-07-30-haeundae-lunch',
		city: 'busan',
		date: '2026-07-30',
		start: '11:30',
		end: '13:00',
		title: 'Drive to Haeundae + lunch',
		category: 'food',
		cost: { krw: 15000, zar: 161 },
	},
	{
		id: '2026-07-30-skycapsule',
		city: 'busan',
		date: '2026-07-30',
		start: '13:00',
		end: '15:00',
		title: 'Haeundae Blueline Park — Sky Capsule + Beach Train',
		summary: 'Pastel capsules along the coast. BOOK ONLINE — it sells out.',
		category: 'sightseeing',
		cost: { krw: 17500, zar: 188, note: '₩35k per 2-seat capsule' },
		location: loc('Haeundae Blueline Park Sky Capsule', 'Haeundae Blueline Park Sky Capsule'),
	},
	{
		id: '2026-07-30-haeundae-beach',
		city: 'busan',
		date: '2026-07-30',
		start: '15:00',
		end: '17:30',
		title: 'Haeundae Beach + Dongbaek / Nurimaru',
		category: 'nature',
		cost: { krw: 0, zar: 0 },
		location: loc('Haeundae Beach', 'Haeundae Beach'),
	},
	{
		id: '2026-07-30-bay101',
		city: 'busan',
		date: '2026-07-30',
		start: '18:00',
		end: '20:00',
		title: 'The Bay 101 — night yacht + skyline, dinner',
		summary: 'Marine City skyline reflection. Yacht ~₩20k.',
		category: 'show',
		cost: { krw: 20000, zar: 215 },
		location: loc('The Bay 101', 'The Bay 101 Busan yacht'),
	},

	// Fri 31 Jul — Gwangalli + baseball
	{
		id: '2026-07-31-gwangalli',
		city: 'busan',
		date: '2026-07-31',
		start: '11:00',
		end: '17:00',
		title: 'Gwangalli Beach — relaxed day',
		summary: 'Gwangan Bridge views, cafés. (Drone show is Sat-only — not tonight.)',
		category: 'nature',
		cost: { krw: 0, zar: 0 },
		location: loc('Gwangalli Beach', 'Gwangalli Beach Busan'),
	},
	{
		id: '2026-07-31-baseball',
		city: 'busan',
		date: '2026-07-31',
		start: '18:30',
		end: '21:30',
		title: 'Lotte Giants @ Sajik Stadium ⚾',
		summary: 'Raucous, cheap, fun. VERIFY the home schedule — move to 28–30 Jul if away.',
		category: 'sport',
		cost: { krw: 13000, zar: 140, note: 'verify home dates' },
		location: loc('Sajik Baseball Stadium', 'Sajik Baseball Stadium Busan'),
	},
	{
		id: '2026-07-31-arrange-taxi',
		city: 'busan',
		date: '2026-07-31',
		start: '21:45',
		end: '22:00',
		title: '⏰ Arrange tomorrow’s taxi to Busan Station with the hotel',
		summary:
			'Ask the hotel tonight to book a taxi for the run to Busan Station for the 15:54 train to Seoul.',
		category: 'travel',
	},

	// Sat 1 Aug — Busan → Seoul → Incheon → Home
	{
		id: '2026-08-01-morning',
		city: 'busan',
		date: '2026-08-01',
		start: '09:00',
		end: '13:30',
		title: 'Easy morning in Busan, check out',
		summary: 'Breakfast, pack, one last wander near Busan Station. Check out and take the pre-arranged taxi to the station.',
		category: 'relax',
		cost: { krw: 0, zar: 0 },
	},
	{
		id: '2026-08-01-ktx',
		city: 'busan',
		date: '2026-08-01',
		start: '15:54',
		end: '18:49',
		title: 'Train Busan → Seoul Station',
		summary: '~2h55, arriving Seoul 18:49. Book in advance.',
		category: 'travel',
		cost: { krw: 59800, zar: 643 },
		location: loc('Busan Station', 'Busan Station KTX'),
	},
	{
		id: '2026-08-01-taxi-icn',
		city: 'busan',
		date: '2026-08-01',
		start: '19:00',
		end: '20:15',
		title: 'Taxi Seoul → Incheon Airport (ICN T1)',
		summary: 'Taxi from Seoul Station out to Incheon for the late flight home.',
		category: 'travel',
		location: loc('Incheon Airport T1', 'Incheon Airport Terminal 1'),
	},
	{
		id: '2026-08-01-icn-dxb',
		city: 'busan',
		date: '2026-08-01',
		start: '23:55',
		end: '23:59',
		title: 'Fly Seoul → Dubai · EK323',
		summary:
			'Check-in 20:40 (KST), Incheon Terminal 1. Depart ICN 23:55 KST → arrive Dubai (DXB) 2 Aug 04:25 GST. Emirates back to South Africa.',
		category: 'travel',
		location: loc('Incheon International Airport T1', 'Incheon International Airport Terminal 1'),
	},

	// Sun 2 Aug — Dubai → Johannesburg (home)
	{
		id: '2026-08-02-dxb-jnb',
		city: 'busan',
		date: '2026-08-02',
		start: '09:55',
		end: '16:15',
		title: 'Fly Dubai → Johannesburg · EK763',
		summary:
			'Check-in 06:55 (GST). Depart Dubai (DXB) 09:55 GST → arrive OR Tambo (JNB) 16:15 SAST. Trip done 🎉',
		category: 'travel',
		location: loc('OR Tambo International Airport', 'OR Tambo International Airport Johannesburg'),
	},
];
